// A line diff for showing how a card's text changed (DESIGN.md 信任層:
// per-item before/after). Plain longest-common-subsequence over lines: card
// texts are short, so the quadratic table is fine.

export type DiffLine = { op: 'same' | 'add' | 'del'; text: string }

/** Lines of `a` turned into lines of `b`, in reading order. */
export function lineDiff(a: string, b: string): DiffLine[] {
  const x = a ? a.split('\n') : []
  const y = b ? b.split('\n') : []
  const n = x.length
  const m = y.length
  // lcs[i][j]: length of the common subsequence of x[i..] and y[j..].
  const lcs = Array.from({ length: n + 1 }, () => new Array<number>(m + 1).fill(0))
  for (let i = n - 1; i >= 0; i--) {
    const row = lcs[i] as number[]
    const next = lcs[i + 1] as number[]
    for (let j = m - 1; j >= 0; j--)
      row[j] =
        x[i] === y[j]
          ? (next[j + 1] as number) + 1
          : Math.max(next[j] as number, row[j + 1] as number)
  }
  const out: DiffLine[] = []
  let i = 0
  let j = 0
  while (i < n && j < m) {
    if (x[i] === y[j]) {
      out.push({ op: 'same', text: x[i] as string })
      i++
      j++
    } else if ((lcs[i + 1]?.[j] ?? 0) >= (lcs[i]?.[j + 1] ?? 0)) {
      out.push({ op: 'del', text: x[i++] as string })
    } else {
      out.push({ op: 'add', text: y[j++] as string })
    }
  }
  while (i < n) out.push({ op: 'del', text: x[i++] as string })
  while (j < m) out.push({ op: 'add', text: y[j++] as string })
  return out
}

/**
 * The diff with long unchanged stretches folded to `context` lines around each
 * change; a folded stretch is one `{ op: 'same', text: '' }` gap marker with
 * `folded` set to how many lines it hides.
 */
export function foldDiff(lines: DiffLine[], context = 2): (DiffLine & { folded?: number })[] {
  const near = lines.map((_, k) =>
    lines.slice(Math.max(0, k - context), k + context + 1).some((l) => l.op !== 'same'),
  )
  const out: (DiffLine & { folded?: number })[] = []
  let hidden = 0
  lines.forEach((l, k) => {
    if (near[k]) {
      if (hidden) out.push({ op: 'same', text: '', folded: hidden })
      hidden = 0
      out.push(l)
    } else hidden++
  })
  if (hidden) out.push({ op: 'same', text: '', folded: hidden })
  return out
}
